'use client';

import { Switch } from '@sprd/sprd-component-kit/v2';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useAtom, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import {
  embroideryProductKeyAtom,
  pdpGalleryOffsetDurationAtom,
  showEmbroideryOnDetailPageAtom,
} from '@/store/atoms';
import { EMBROIDERY_PRINT_TYPE_IDS } from '@/utility/constants';
import { precheckEmbroidery, previewEmbroidery } from '@/utility/embroideryHelpers';
import { tracker } from '@/utility/tracking/tracker';

const EMBROIDERY_SWITCH_FADE_DURATION_MS = 600;

export type EmbroideryButtonProps = {
  publicApiConfig: PublicApiConfig;
  sellable: Sellable;
  designerMode: string;
  printTypeIds: string[];
  observerTargetId?: string;
};

export const EmbroiderySwitch = ({
  publicApiConfig,
  sellable,
  designerMode,
  printTypeIds,
  observerTargetId,
}: EmbroideryButtonProps) => {
  const t = useTranslations();
  const [embroideryProductKey, setEmbroideryProductKey] = useAtom(embroideryProductKeyAtom);
  const [showEmbroidery, setShowEmbroidery] = useAtom(showEmbroideryOnDetailPageAtom);
  const setEmbroideryMobileUiOffset = useSetAtom(pdpGalleryOffsetDurationAtom);
  const [isActive, setIsActive] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isParentInView, setIsParentInView] = useState(true);
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);
  const [hasBeenThreeSeconds, setHasBeenThreeSeconds] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setHasBeenThreeSeconds(true);
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!isDesktop || !isActive) {
      setIsParentInView(true);
      return;
    }

    const observedTarget = observerTargetId && document.getElementById(observerTargetId);

    if (!observedTarget) {
      setIsParentInView(true);
      return;
    }

    let rafId: number;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          // Reset first so the component starts off-screen, then rAF triggers the slide-in.
          setHasAnimatedIn(false);
          setIsParentInView(true);
          rafId = window.requestAnimationFrame(() => {
            setHasAnimatedIn(true);
          });
        } else {
          window.cancelAnimationFrame(rafId);
          setHasAnimatedIn(false);
          setIsParentInView(false);
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(observedTarget);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(rafId);
    };
  }, [isDesktop, isActive, observerTargetId]);

  useEffect(() => {
    if (!isActive || (isDesktop && !hasBeenThreeSeconds)) {
      setHasAnimatedIn(false);
      return;
    }

    // Initial slide-in when the component first becomes active.
    const frameId = window.requestAnimationFrame(() => {
      setHasAnimatedIn(true);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isActive, isDesktop, hasBeenThreeSeconds]);

  useEffect(() => {
    if (!isDesktop && isActive) {
      setEmbroideryMobileUiOffset(EMBROIDERY_SWITCH_FADE_DURATION_MS);
    } else {
      setEmbroideryMobileUiOffset(0);
    }
  }, [isActive, isDesktop, setEmbroideryMobileUiOffset]);

  useEffect(() => {
    const updateIsActive = async () => {
      const allowsEmbroideryPrintType = EMBROIDERY_PRINT_TYPE_IDS.some((id) =>
        printTypeIds.includes(id)
      );
      const isCustomizationAllowed = designerMode === 'ENABLED';

      // Digital print types have an empty array for printColorIds
      const hasDigitalPrintTypesOnly = !sellable.printAreas
        .flatMap((printArea) => printArea.printTypes)
        .some((printType) => printType.printColorIds.length > 0);

      if (!allowsEmbroideryPrintType || !isCustomizationAllowed || !hasDigitalPrintTypesOnly) {
        setIsActive(false);
        return;
      }

      const precheckResult = await precheckEmbroidery(publicApiConfig, sellable.vpString);
      setIsActive(Boolean(precheckResult));

      if (precheckResult) {
        tracker.trackFeature({
          featureUsed: 'embroidery-toggle-view',
          detail: 'visible',
        });
      }
    };

    void updateIsActive();
  }, [designerMode, printTypeIds, publicApiConfig, sellable.printAreas, sellable.vpString]);

  const swapEmbroidery = (checked: boolean) => {
    const preview = async () => {
      const result = await previewEmbroidery(publicApiConfig, sellable.vpString);
      if (result.productKey) {
        setEmbroideryProductKey(result.productKey);
        setShowEmbroidery(true);
        trackEmbroidery(true);
      }
    };
    if (checked) {
      if (embroideryProductKey) {
        setShowEmbroidery(true);
        trackEmbroidery(true);
      } else {
        preview();
      }
    } else {
      setShowEmbroidery(false);
      trackEmbroidery(false);
    }
  };

  const trackEmbroidery = (on: boolean) => {
    tracker.trackFeature({ featureUsed: 'embroidery-toggle', detail: on ? 'on' : 'off' });
    tracker.trackClientProduct({ product: { embroidery: on } });
  };

  const isDesktopVisible = !isDesktop || (hasAnimatedIn && isParentInView);

  return (
    isActive && (
      <div
        ref={containerRef}
        style={{ transitionDuration: `${EMBROIDERY_SWITCH_FADE_DURATION_MS}ms` }}
        className={clsx(
          'absolute bottom-0 inline-flex h-10 w-full flex-wrap content-center items-center justify-center gap-2 bg-black px-4 text-white motion-safe:transition-opacity motion-safe:ease-out',
          hasAnimatedIn ? 'opacity-90' : 'opacity-0',
          'md:fixed md:bottom-6 md:left-1/2 md:z-20 md:h-11 md:w-fit md:-translate-x-1/2 md:transform md:rounded-4xl md:bg-white md:text-inherit md:shadow-md md:outline-1 motion-safe:md:transition-all motion-safe:md:duration-300 motion-safe:md:ease-out',
          isDesktopVisible
            ? 'md:pointer-events-auto md:translate-y-0 md:opacity-90'
            : 'md:pointer-events-none md:translate-y-full md:opacity-0'
        )}
      >
        <Switch
          className={
            'w-full items-center justify-between text-xs md:w-auto md:justify-normal md:text-sm'
          }
          label={t('mpDetailPage.embroidery.toggle')}
          labelPosition={'left'}
          checked={showEmbroidery}
          variant={'success'}
          onChange={(event) => swapEmbroidery(event.currentTarget.checked)}
        ></Switch>
      </div>
    )
  );
};
